// Log Downloader Component
// Allows users to download error logs even when no error is visible
// Access via keyboard shortcut: Ctrl+Shift+L (or Cmd+Shift+L on Mac)

import React, { useState, useEffect } from 'react';
import logCollector from '../utils/logger';
import consentManager from '../utils/consentManager';

function LogDownloader() {
  const [isVisible, setIsVisible] = useState(false);
  const [logCount, setLogCount] = useState(0);
  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    // Update log count and consent status
    const updateStatus = () => {
      setLogCount(logCollector.getAllLogs().length);
      setHasConsent(consentManager.hasConsent());
    };
    updateStatus();

    // Keyboard shortcut: Ctrl+Shift+L (or Cmd+Shift+L on Mac)
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'L') {
        e.preventDefault();
        setIsVisible(prev => !prev);
        updateStatus();
      }
      
      // Close on Escape
      if (e.key === 'Escape' && isVisible) {
        setIsVisible(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible]);

  const handleDownload = (format) => {
    try {
      if (format === 'json') {
        logCollector.exportLogsAsJSON();
      } else {
        logCollector.exportLogsAsText();
      }
      
      // Show success feedback
      const button = document.getElementById(`log-download-${format}`);
      if (button) {
        const originalText = button.textContent;
        button.textContent = '✓ Downloaded!';
        button.style.backgroundColor = '#9b7fa8';
        setTimeout(() => {
          button.textContent = originalText;
          button.style.backgroundColor = '#c6a7cf';
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to download logs:', error);
      alert('Failed to download logs. Please try again.');
    }
  };

  const handleClearLogs = () => {
    if (window.confirm('Are you sure you want to clear all logs? This cannot be undone.')) {
      logCollector.clearLogs();
      setLogCount(0);
      alert('Logs cleared successfully.');
    }
  };

  const handleRevokeConsent = () => {
    if (window.confirm('Are you sure you want to revoke consent? This will stop error logging and clear all stored logs.')) {
      consentManager.revokeConsent();
      logCollector.revokeConsent();
      setHasConsent(false);
      setLogCount(0);
      alert('Consent revoked. Error logging has been disabled.');
    }
  };

  const handleGrantConsent = () => {
    if (consentManager.grantConsent()) {
      logCollector.initializeWithConsent();
      setHasConsent(true);
      alert('Consent granted. Error logging is now active.');
      // Reload to fully activate logging
      window.location.reload();
    } else {
      alert('Failed to grant consent. Please try again.');
    }
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(10, 10, 10, 0.85)',
      backdropFilter: 'blur(8px)',
      zIndex: 9999,
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}
    onClick={(e) => {
      if (e.target === e.currentTarget) {
        setIsVisible(false);
      }
    }}
    >
      <div style={{
        maxWidth: '500px',
        width: '100%',
        backgroundColor: '#1a1a1a',
        border: '4px solid #c6a7cf',
        borderRadius: '0',
        padding: '32px',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(198, 167, 207, 0.4), 0 0 30px rgba(198, 167, 207, 0.3)',
        color: '#fff',
        position: 'relative'
      }}>
        <button
          onClick={() => setIsVisible(false)}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#c6a7cf',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '4px 8px',
            lineHeight: 1
          }}
          onMouseOver={(e) => e.target.style.opacity = '0.7'}
          onMouseOut={(e) => e.target.style.opacity = '1'}
        >
          ×
        </button>

        <h2 style={{
          fontSize: '24px',
          marginBottom: '16px',
          fontWeight: '700',
          color: '#c6a7cf',
          letterSpacing: '-0.5px'
        }}>
          📥 Download Error Logs
        </h2>

        <p style={{
          fontSize: '14px',
          marginBottom: '24px',
          color: 'rgba(255, 255, 255, 0.8)',
          lineHeight: '1.6'
        }}>
          Download all stored error logs and console messages to share with developers.
        </p>

        {/* Consent Status */}
        <div style={{
          backgroundColor: hasConsent ? 'rgba(141, 255, 164, 0.1)' : 'rgba(255, 107, 107, 0.1)',
          padding: '16px',
          borderRadius: '0',
          marginBottom: '24px',
          border: `2px solid ${hasConsent ? 'rgba(141, 255, 164, 0.3)' : 'rgba(255, 107, 107, 0.3)'}`
        }}>
          <p style={{
            fontSize: '14px',
            fontWeight: '600',
            color: hasConsent ? '#8DFFA4' : '#ff6b6b',
            margin: '0 0 8px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {hasConsent ? '✓' : '✗'} 
            {hasConsent ? 'Consent Granted' : 'Consent Not Granted'}
          </p>
          <p style={{
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.6)',
            margin: '0 0 12px 0'
          }}>
            {hasConsent 
              ? 'Error logging is active. Your data helps us improve the site.' 
              : 'Error logging is disabled. Grant consent to enable logging.'}
          </p>
          {hasConsent ? (
            <button
              onClick={handleRevokeConsent}
              style={{
                backgroundColor: 'transparent',
                color: '#ff6b6b',
                border: '1px solid #ff6b6b',
                padding: '6px 12px',
                fontSize: '11px',
                fontWeight: '600',
                borderRadius: '0',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Revoke Consent
            </button>
          ) : (
            <button
              onClick={handleGrantConsent}
              style={{
                backgroundColor: '#8DFFA4',
                color: '#0a0a0a',
                border: '1px solid #8DFFA4',
                padding: '6px 12px',
                fontSize: '11px',
                fontWeight: '600',
                borderRadius: '0',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Grant Consent
            </button>
          )}
        </div>

        <div style={{
          backgroundColor: 'rgba(198, 167, 207, 0.1)',
          padding: '16px',
          borderRadius: '0',
          marginBottom: '24px',
          border: '2px solid rgba(198, 167, 207, 0.3)'
        }}>
          <p style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#c6a7cf',
            margin: '0 0 8px 0'
          }}>
            {logCount} {logCount === 1 ? 'Log Entry' : 'Log Entries'}
          </p>
          <p style={{
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.6)',
            margin: 0
          }}>
            Stored in browser cache
          </p>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          marginBottom: '16px'
        }}>
          <button
            id="log-download-json"
            onClick={() => handleDownload('json')}
            disabled={!hasConsent || logCount === 0}
            style={{
              backgroundColor: (!hasConsent || logCount === 0) ? 'rgba(198, 167, 207, 0.3)' : '#c6a7cf',
              color: '#0a0a0a',
              border: '2px solid #c6a7cf',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '700',
              borderRadius: '0',
              cursor: (!hasConsent || logCount === 0) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxShadow: '0 2px 8px rgba(198, 167, 207, 0.3)',
              opacity: (!hasConsent || logCount === 0) ? 0.5 : 1
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#d4b8dd';
              e.target.style.borderColor = '#d4b8dd';
              e.target.style.boxShadow = '0 3px 12px rgba(198, 167, 207, 0.5)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#c6a7cf';
              e.target.style.borderColor = '#c6a7cf';
              e.target.style.boxShadow = '0 2px 8px rgba(198, 167, 207, 0.3)';
            }}
          >
            💾 Download as JSON
          </button>

          <button
            id="log-download-text"
            onClick={() => handleDownload('text')}
            disabled={!hasConsent || logCount === 0}
            style={{
              backgroundColor: (!hasConsent || logCount === 0) ? 'rgba(198, 167, 207, 0.3)' : '#c6a7cf',
              color: '#0a0a0a',
              border: '2px solid #c6a7cf',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '700',
              borderRadius: '0',
              cursor: (!hasConsent || logCount === 0) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxShadow: '0 2px 8px rgba(198, 167, 207, 0.3)',
              opacity: (!hasConsent || logCount === 0) ? 0.5 : 1
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#d4b8dd';
              e.target.style.borderColor = '#d4b8dd';
              e.target.style.boxShadow = '0 3px 12px rgba(198, 167, 207, 0.5)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#c6a7cf';
              e.target.style.borderColor = '#c6a7cf';
              e.target.style.boxShadow = '0 2px 8px rgba(198, 167, 207, 0.3)';
            }}
          >
            📄 Download as Text
          </button>
        </div>

        {logCount > 0 && (
          <button
            onClick={handleClearLogs}
            style={{
              backgroundColor: 'transparent',
              color: '#ff6b6b',
              border: '2px solid #ff6b6b',
              padding: '8px 16px',
              fontSize: '12px',
              fontWeight: '600',
              borderRadius: '0',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              width: '100%'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 107, 107, 0.1)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            🗑️ Clear All Logs
          </button>
        )}

        <p style={{
          marginTop: '20px',
          fontSize: '11px',
          color: 'rgba(198, 167, 207, 0.6)',
          fontStyle: 'italic'
        }}>
          Press <kbd style={{
            backgroundColor: 'rgba(198, 167, 207, 0.2)',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '10px'
          }}>Ctrl+Shift+L</kbd> to open this panel
        </p>
      </div>
    </div>
  );
}

export default LogDownloader;

